import { Breadcrumbs } from "./breadcrumbs";
import { Search } from "./search";
import { Api } from "@/api";
import { CurrencyService } from "./currency-service";
import { Formatter } from "./formatter";
import { CustomerChannel } from "./customer-channel";
import { Main } from "@/context/main.ts";
import { Customer } from "@/context/customer.ts";
import { Cart } from "@/context/cart.ts";
import { Categories } from "./categories";
import { StaticPages } from "@/context/static-pages.ts";
import { Countries } from "./countries.ts";
import { Routes } from "./routes";
import { Brands } from "./brands";
import { Initialize } from "./initialize.ts";
import { Parameters } from "./parameters.ts";
import i18next from "i18next";
import { PageMeta } from "./pagemeta.ts";
import { JsonLd } from "./json-ld.ts";
import { Cache } from "./cache.ts";
import { Values } from "./values.ts";
import { Alerts } from "@/context/alerts";
import { Holidays } from "@/services/holidays.ts";
import { defaultVatCoefficient } from "@/constants/vat.ts";
import { SeoMarkup } from "./seo-markup.ts";
import { Cookies } from "./cookies.ts";
import { Preloaded } from "./preloaded.ts";
import { Router, RoutesMap } from "./router.ts";
import { Context } from "./context.ts";
import { i18n } from "./i18n.ts";
import { Suspense } from "./suspense";
import { Entries } from "@/elements-react/document-elements/head-tags.tsx";
import {FeaturedProducts} from "@/services/featured-products.ts";
import { BackendTranslations } from "@/generated/graphql/b2c";

export class Services {
  private host!: Element;
  public breadcrumbs!: Breadcrumbs;
  public search!: Search;
  public mainContext!: Main;
  public currency!: CurrencyService;
  public formatter!: Formatter;
  public customerChannel!: CustomerChannel;
  public customer!: Customer;
  public cart!: Cart;
  public alerts!: Alerts;
  public categories!: Categories;
  public brands!: Brands;
  public api!: Api;
  public staticPages!: StaticPages;
  public countries!: Countries;
  public params: Map<string, string>;
  public routes!: Routes;
  public initialize!: Initialize;
  public parameters!: Parameters;
  public pagemeta!: PageMeta;
  public jsonld!: JsonLd;
  public cache!: Cache;
  public values!: Values;
  public holidays!: Holidays;
  public seoMarkup!: SeoMarkup;
  public cookies!: Cookies;
  public preloaded!: Preloaded;
  public router!: Router;
  public context!: Context;
  public i18n!: i18n;
  public suspense!: Suspense;
  public entries!: Entries;
  public featuredProducts!: FeaturedProducts;
  public backendTranslations!: BackendTranslations;

  constructor(host: Element, api: Api, entries: Entries, routesMap: RoutesMap = {}) {
    this.params = new Map();
    Services.instance = this;
    this.host = host;
    this.context = new Context();
    this.entries = entries;

    this.mainContext = new Main(this.context, api);
    this.breadcrumbs = new Breadcrumbs(this.context, api);
    this.search = new Search(this.context, api);
    this.currency = new CurrencyService(this.context, api);
    this.formatter = new Formatter(this.context, this.currency);
    this.customerChannel = new CustomerChannel(this.context, api, this);
    this.categories = new Categories(this.context, api);
    this.brands = new Brands(api);
    this.customer = new Customer(this.context, api);
    this.cart = new Cart(this.context, api);
    this.alerts = new Alerts(this.context);
    this.countries = new Countries(this.context, this.customerChannel, this.formatter, this.customer, this);
    this.api = api;
    this.routes = new Routes(this);
    this.initialize = new Initialize(this);
    this.parameters = new Parameters(this.context, this);
    this.pagemeta = new PageMeta();
    this.jsonld = new JsonLd(this);
    this.cache = new Cache();
    this.values = new Values();
    this.holidays = new Holidays();
    this.seoMarkup = new SeoMarkup(this);
    this.cookies = new Cookies();
    this.preloaded = new Preloaded(this);
    this.router = new Router();
    this.router.setupRoutesMap(routesMap);
    this.staticPages = new StaticPages(this.context, api);
    this.i18n = new i18n();
    this.suspense = new Suspense();
    this.featuredProducts = new FeaturedProducts(this.context, api);
  }

  static instance: Services = null!;

  static setParams(params: Record<string, string>, clear = false) {
    if (clear) {
      this.instance.params.clear();
    }

    for (const [key, value] of Object.entries(params)) {
      this.instance.params.set(key, value);
    }
  }

  static getParam(key: string) {
    return this.instance.params.get(key);
  }

  setLoading(loading: boolean) {
    this.host.dispatchEvent(new CustomEvent("loading", { detail: { loading }, bubbles: true, composed: true }));
  }
}
